import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useBoolean } from 'react-use'
import { isFilterActive } from '@/services/utils'
import clsx from 'clsx'

import { OrgPermission } from '@makyoapp/makyo-sdk/models/ACL'
import { ProjectAdapter } from '@makyoapp/makyo-common'
import { DeleteModal, Modal } from '@makyoapp/makyo-ui'

import { useOrgAppsContext } from '@/context/OrgAppsContext'
import { useOrgPerm } from '@/hooks/useOrgPerm'
import { Info } from '@/components/Info'
import PricingTable from '@/components/PricingTable'

import ProjectItem from './ProjectItem'

interface ProjectListProps {
  /**
   * list of apps
   */
  apps: ProjectAdapter[]
  /**
   *
   */
  isLoading: boolean
  /**
   * handle app delete
   * @param id
   * @param closeModal
   */
  handleDelete: (id: string, closeModal: () => void) => void
}

/**
 * list project in cards view
 *
 * @param apps
 * @param handleDelete
 *
 * @todo Add UI for empty search
 */
export function ProjectList({ apps, isLoading, handleDelete }: ProjectListProps) {
  // CONTEXTS
  const { t } = useTranslation()
  const { search, filter, resetFilter } = useOrgAppsContext()
  const [isDelLoading, setIsDelLoading] = useBoolean(false)
  const [showPlanModal, setShowPlanModal] = useState<boolean>(false)
  const [changePlanApp, setChangePlanApp] = useState<ProjectAdapter | null>(null)
  const [deletedApp, setDeletedApp] = useState<ProjectAdapter | undefined>(undefined)

  const closeDeleteModal = useCallback(() => {
    setDeletedApp(undefined)
    setIsDelLoading(false)
  }, [setDeletedApp, setIsDelLoading])

  const handleSubmit = useCallback(async () => {
    if (deletedApp) {
      setIsDelLoading(true)
      const appToDeleteId = deletedApp.id
      await handleDelete(appToDeleteId, closeDeleteModal)
    }
  }, [deletedApp, handleDelete, setIsDelLoading, closeDeleteModal])

  const canAddNewApp = useOrgPerm(OrgPermission.AppsCreateNewApp)

  const hasFilter = isFilterActive(filter)

  return (
    <>
      {!isLoading && hasFilter && (
        <Info className='w-full p-4 border bdr mb-8 rounded-sm'>
          Are you missing some apps?{' '}
          <button className='link font-bold' onClick={resetFilter}>
            Reset
          </button>{' '}
          app filter.
        </Info>
      )}

      {!apps.length && search && <Info className='w-full p-4 border bdr mb-8 rounded-sm'>{t('No apps found')}</Info>}

      <div className='grids-3 gap-8'>
        {apps.map((app) => {
          return (
            <ProjectItem
              app={app}
              key={`app-${app.id}`}
              setChangePlanApp={setChangePlanApp}
              setShowPlanModal={setShowPlanModal}
              setDelete={setDeletedApp}
              apps={apps}
            />
          )
        })}

        {canAddNewApp && (
          <Link
            to='./add-new-app'
            className={clsx(
              'flex-cc border bdr p-4 rounded-sm bg-white dark:bg-transparent min-h-[6rem] text-primary-500',
              'hover:bg-primary-50',
              'dark:hover:text-white  dark:hover:bg-slate-700'
            )}
          >
            {t('+ Add new app')}
          </Link>
        )}

        <Modal show={showPlanModal} onHide={() => setShowPlanModal(false)}>
          <Modal.Header onCloseClick={() => setShowPlanModal(false)}>{t('Change Plan')}</Modal.Header>
          <Modal.Body noPadding={true}>
            {changePlanApp && (
              <div className='w-[80vw] block p-8'>
                <PricingTable
                  activePlan={changePlanApp.plan.name}
                  onClick={() => {
                    setShowPlanModal(false)
                  }}
                />
              </div>
            )}
          </Modal.Body>
        </Modal>

        {deletedApp && (
          <DeleteModal
            show={true}
            onHide={() => setDeletedApp(undefined)}
            onSubmit={handleSubmit}
            title={t('Remove App')}
            isLoading={isDelLoading}
            continueLabel={t('Yes, remove app')}
            withCancel
          >
            <div className='text-sm mb-2'>{t("Are you sure you'd like to remove this app?")}</div>
            <div className='text-light'>
              {t('Deleted apps can be found in the trash and be restored within 14 days.')}
            </div>
          </DeleteModal>
        )}
      </div>
    </>
  )
}
